import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useUserStore } from '@/store/modules/user';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

// 配置 NProgress
NProgress.configure({ showSpinner: false });

export const constantRoutes: RouteRecordRaw[] = [
  {
    path: '/error',
    component: () => import('@/views/error.vue'),
    meta: { hidden: true }
  },
  {
    path: '/login',
    component: () => import('@/views/login/index.vue'),
    meta: { hidden: true }
  },
  {
    path: '/',
    component: () => import('@/layout/main-layout.vue'),
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        name: 'Dashboard',
        meta: { title: '首页', icon: 'icon-dashboard' }
      },
      {
        path: 'cs',
        name: 'CustomerService',
        meta: { title: '客户服务' },
        children: [
          {
            path: 'customers',
            name: 'CustomerList',
            component: () => import('@/views/cs/customers.vue'),
            meta: { title: '客户列表' }
          },
          {
            path: 'activity',
            name: 'ActivityCenter',
            meta: { title: '平台活动' },
            children: [
              {
                path: 'list',
                name: 'ActivityList',
                component: () => import('@/views/sale/activities.vue'),
                meta: { title: '活动列表' },
              },
              {
                path: 'members',
                name: 'ActivityReview',
                component: () => import('@/views/activity/activity-members.vue'),
                meta: { title: '报名审核' },
              },
              {
                path: 'activity-members',
                name: 'ActivityMembers',
                component: () => import('@/views/activity/activity-members.vue'),
                meta: { title: '活动成员管理' },
              },
            ],
          },
          {
            path: 'museums',
            name: 'MuseumList',
            component: () => import('@/views/cs/museums.vue'),
            meta: {
              locale: "menu.museums",
              requiresAuth: true,
              roles: ["*"],
            },
          },
          {
            path: 'museums/edit',
            name: 'MuseumCreate',
            component: () => import('@/views/cs/components/museum-edit.vue'),
            meta: {
              title: '新增场馆',
              requiresAuth: true,
              roles: ["*"],
            }
          },
          {
            path: 'museums/edit/:id',
            name: 'MuseumEdit',
            component: () => import('@/views/cs/components/museum-edit.vue'),
            meta: {
              title: '编辑场馆',
              requiresAuth: true,
              roles: ["*"],
            }
          },
          {
            path: 'museums/:id',
            name: 'museum-detail',
            component: () => import('@/views/cs/museum-detail.vue'),
            meta: {
              locale: "menu.museum.detail",
              requiresAuth: true,
              roles: ["*"],
            },
          },

          {
            path: 'museums/:id',
            name: 'MuseumDetail',
            component: () => import('@/views/cs/museum-detail.vue'),
            meta: { title: '场馆详情' },
            children: [
              {
                path: 'edit',
                name: 'museum-edit',
                component: () => import('@/views/cs/components/museum-edit.vue'),
                meta: {
                  title: '场馆编辑',
                  locale: "menu.museum.edit",
                  requiresAuth: true,
                  roles: ["*"],
                },
              },
              {
                path: 'exhibits',
                name: 'MuseumExhibits',
                component: () => import('@/views/cs/components/museum-exhibits.vue'),
                meta: { title: '展品管理' }
              },
              {
                path: 'routes',
                name: 'MuseumRoutes',
                component: () => import('@/views/cs/components/museum-itinerary.vue'),
                meta: { title: '主题线路' }
              },
              {
                path: 'pois',
                name: 'MuseumPOIs',
                component: () => import('@/views/cs/components/museum-poi.vue'),
                meta: { title: 'POI管理' }
              },
              {
                path: 'maps',
                name: 'MuseumMaps',
                component: () => import('@/views/cs/components/museum-map.vue'),
                meta: { title: '地图管理' }
              },
              {
                path: 'knowledge',
                name: 'MuseumKnowledge',
                component: () => import('@/views/cs/components/museum-knowledge.vue'),
                meta: { title: '知识库' }
              },
              {
                path: 'tour-itinerary/new',
                name: 'TourItineraryCreate',
                component: () => import('@/views/cs/components/tour-itinerary-edit.vue'),
                meta: { title: '新增导游行程' }
              },
              {
                path: 'tour-itinerary/:itineraryId',
                name: 'TourItineraryEdit',
                component: () => import('@/views/cs/components/tour-itinerary-edit.vue'),
                meta: { title: '编辑导游行程' }
              },
              {
                path: 'areas',
                name: 'MuseumAreas',
                component: () => import('@/views/cs/components/museum-areas.vue'),
                meta: { title: '展区管理' }
              },
              {
                path: 'areas/new',
                name: 'MuseumAreaCreate',
                component: () => import('@/views/cs/components/museum-area-edit.vue'),
                meta: { title: '新增展区' }
              },
              {
                path: 'areas/:areaId',
                name: 'MuseumAreaEdit',
                component: () => import('@/views/cs/components/museum-area-edit.vue'),
                meta: { title: '编辑展区' }
              }
            ]
          },
          {
            path: 'docents',
            name: 'DocentList',
            meta: { title: '讲解员管理' },
            component: () => import('@/views/cs/docents.vue'),
          },
          {
            path: 'client-profile',
            name: 'ClientProfile',
            component: () => import('@/views/cs/client-profile.vue'),
            meta: { title: '客群定义' }
          },
          {
            path: 'docent-scenic-script',
            name: 'DocentPromptList',
            meta: { title: '讲解员提示词管理' },
            component: () => import('@/views/cs/docent-scenic-script.vue'),
          },
          {
            path: 'tour-itinerary/:id/exhibits',
            name: 'TourItineraryExhibits',
            meta: { title: '导游行程展品管理' },
            component: () => import('@/views/cs/exhibits.vue'),
          }, {
            path: 'tour-itinerary',
            name: 'TourItinerary',
            meta: { title: '导游行程列表' },
            component: () => import('@/views/cs/components/museum-itinerary.vue'),
          },

          {
            path: 'exhibits',
            name: 'exhibits',
            component: () => import('@/views/cs/exhibits.vue'),
            meta: { title: '展品' }
          },
          {
            path: 'knowledge-list/:id',
            name: 'CustomerKnowledgeList',
            component: () => import('@/views/cs/customer-knowledges.vue'),
            meta: { title: '客户知识库' }
          },
        ]
      },
      {
        path: 'knowledge',
        name: 'KnowledgeCenter',
        meta: { title: '知识图谱' },
        children: [
          {
            path: 'generate',
            name: 'ContentGenerate',
            component: () => import('@/views/knowledge/generate.vue'),
          },
          {
            path: 'base',
            name: 'KnowledgeBase',
            component: () => import('@/views/knowledge/base.vue'),
            meta: { title: '垂类知识库' }
          },
          {
            path: 'hot-news',
            name: 'HotNews',
            component: () => import('@/views/knowledge/hot-news.vue'),
            meta: { title: '热点资讯' }
          }
        ]
      },
      {
        path: 'member',
        name: 'MemberCenter',
        meta: { title: '用户中心' },
        children: [
          {
            path: 'list',
            name: 'MemberList',
            component: () => import('@/views/member/list.vue'),
            meta: { title: '会员列表' }
          },
          {
            path: 'chat_rec',
            name: 'ChatRec',
            component: () => import('@/views/member/chat_rec.vue'),
            meta: { title: '聊天记录' }
          }
        ]
      },
      {
        path: 'biz',
        name: 'BusinessDevelopment',
        meta: { title: '商务拓展' },
        children: [
          {
            path: 'equipments',
            name: 'EquipmentList',
            component: () => import('@/views/biz/equipments.vue'),
            meta: { title: '设备生产' }
          },
          {
            path: 'applications',
            name: 'ApplicationList',
            component: () => import('@/views/biz/applications.vue'),
            meta: { title: '软件产品' }
          },
          {
            path: 'application/:id/versions',
            name: 'ApplicationVersions',
            component: () => import('@/views/biz/components/application-versions.vue'),
            meta: { title: '软件版本管理' }
          },
          {
            path: 'contracts',
            name: 'ContractList',
            component: () => import('@/views/biz/contracts.vue'),
            meta: { title: '合约' }
          },
          {
            path: 'contract/add',
            name: 'ContractAdd',
            component: () => import('@/views/biz/contract-edit.vue'),
            meta: { title: '新增合约' }
          },
          {
            path: 'contract/edit/:id',
            name: 'ContractEdit',
            component: () => import('@/views/biz/contract-edit.vue'),
            meta: { title: '编辑合约' }
          },
          {
            path: 'build-batches',
            name: 'BuildBatchList',
            component: () => import('@/views/biz/build-batches.vue'),
            meta: { title: '设备批次管理' }
          },
          // {
          //   path: 'opportunities',
          //   name: 'OpportunityList',
          //   component: () => import('@/views/biz/opportunities.vue'),
          //   meta: { title: '商业机会' }
          // }
        ]
      },
      {
        path: 'mobile-client',
        name: 'MobileClient',
        meta: { title: '移动端配置' },
        children: [
          {
            path: 'carousel',
            name: 'Carousel',
            component: () => import('@/views/mobile-client/carousel.vue'),
            meta: { title: '轮播图' }
          },
          {
            path: 'region',
            name: 'Region',
            component: () => import('@/views/mobile-client/region.vue'),
            meta: { title: '区域' }
          }
        ]
      },
      {
        path: 'system',
        name: 'System',
        meta: { title: '系统管理' },
        children: [
          {
            path: 'user',
            name: 'UserManagement',
            component: () => import('@/views/system/user.vue'),
            meta: { title: '用户管理' }
          },
          {
            path: 'role',
            name: 'RoleManagement',
            component: () => import('@/views/system/role.vue'),
            meta: { title: '角色管理' }
          },
          {
            path: 'prompt-templates',
            name: 'PromptTemplates',
            component: () => import('@/views/system/prompt-templates.vue'),
            meta: { title: '提示词模板' }
          },
          {
            path: 'museum-scenes',
            name: 'MuseumScenes',
            component: () => import('@/views/system/museum-scenes.vue'),
            meta: { title: '展馆场景管理' }
          },
          {
            path: 'category',
            name: 'MuseumCategory',
            component: () => import('@/views/system/category.vue'),
            meta: { title: '展馆分类' }
          },
        ]
      },
      {
        path: 'co',
        name: 'ContentOperation',
        meta: { title: '内容运营' },
        children: [
          {
            path: 'generate',
            name: 'ContentList',
            component: () => import('@/views/co/generate.vue'),
            meta: { title: '内容生成' }
          },
          {
            path: 'resources',
            name: 'ResourceList',
            component: () => import('@/views/co/resources.vue'),
            meta: { title: '资源列表' }
          }
        ]
      },
      {
        path: 'sale',
        name: 'SaleService',
        meta: { title: '销售服务' },
        children: [
          {
            path: 'rental-products',
            name: 'RentalProductList',
            component: () => import('@/views/sale/rental-products.vue'),
            meta: { title: '租赁产品' }
          },
          {
            path: 'rental-orders',
            name: 'RentalOrderList',
            component: () => import('@/views/sale/rental-orders.vue'),
            meta: { title: '租赁订单管理' }
          },
          {
            path: 'benefit-products',
            name: 'BenefitProductList',
            component: () => import('@/views/sale/benefit-products.vue'),
            meta: { title: '权益类产品' }
          },
          {
            path: 'activities',
            name: 'ActivityList',
            component: () => import('@/views/sale/activities.vue'),
            meta: { title: '活动管理' }
          },
        ]
      },
    ]
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes: constantRoutes,
  scrollBehavior: () => ({ top: 0 }),
});

// 路由守卫
router.beforeEach(async (to, from, next) => {
  NProgress.start();
  const userStore = useUserStore();
  const token = localStorage.getItem('token');

  if (to.path === "/error") {
    next();
    return;
  }

  if (token) {
    if (to.path === '/login') {
      next({ path: '/dashboard' });
      NProgress.done();
    } else {
      const hasUserInfo = userStore.userInfo;
      if (hasUserInfo) {
        next();
      } else {
        try {
          await userStore.getUserInfo();
          next({ ...to, replace: true });
        } catch (error) {
          userStore.logout();
          next(`/login?redirect=${to.path}`);
        }
      }
    }
  } else {
    if (to.path === '/login') {
      next();
    } else {
      next(`/login?redirect=${to.path}`);
    }
  }
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
